import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import { withFormik, Field } from 'formik';
import Button from '@material-ui/core/Button';
import { TextField } from 'components/FormFields';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { DocumentTypesSelect } from 'components/AsyncFields';

const DocumentModal = ({
  open,
  handleClose,
  errors,
  mode,
  submitForm,
  asyncErrors,
  setErrors,
  setFieldTouched,
  document_type_name
}) => {
  const { isCreatingDocument } = useSelector((state) => state.documents);
  useDeepCompareEffect(() => {
    if (asyncErrors) {
      setErrors(asyncErrors);
      Object.keys(asyncErrors).map((field) => setFieldTouched(field, true, false));
    }
  }, [asyncErrors, setErrors, setFieldTouched]);
  return (
    <BaseModal
      size="sm"
      open={open}
      hasFooter={false}
      hasCancelButton={true}
      enableCloseButton={false}
      aria-labelledby="modal-title"
      title={
        mode === 'create'
          ? i18n.t('documents.create_new_document')
          : i18n.t('documents.edit_document')
      }
    >
      <Box width="100%" mt={2}>
        <Field
          margin="none"
          name="document_type_id"
          id="documentTypesSelect"
          component={DocumentTypesSelect}
          customparams={{ keyword: document_type_name }}
          asyncErrors={errors?.document_type_id}
          label={i18n.t('documents.document_type')}
        />
      </Box>
      <Box my={1}>
        <Button
          style={{ textTransform: 'inherit' }}
          startIcon={<AddCircleIcon />}
          data-cy="add-document-type"
          href={buildUrl('/document-types')}
          target="_blank"
          color="primary"
        >
          {i18n.t('documents.add_document_type')}
        </Button>
      </Box>
      <Box width="100%">
        <Field
          type="text"
          margin="none"
          required={false}
          name="description"
          inputProperties={{}}
          component={TextField}
          asyncErrors={errors?.description}
          label={i18n.t('common.description')}
        />
      </Box>
      <Box mt={5} width="100%" role="group" display="flex" justifyContent="flex-end">
        <Box display="inline" ml={1}>
          <Button
            color="primary"
            onClick={handleClose}
            style={{ letterSpacing: 0.7, fontWeight: 500 }}
            disabled={isCreatingDocument}
          >
            {i18n.t('documents.cancel')}
          </Button>
        </Box>
        <Box display="inline" ml={1}>
          <Button
            type="submit"
            color="primary"
            onClick={submitForm}
            style={{ letterSpacing: 0.7, fontWeight: 500 }}
            disabled={isCreatingDocument}
          >
            {mode === 'create' ? i18n.t('documents.create') : i18n.t('common.save')}
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};

DocumentModal.propTypes = {
  /**
   * Current state modal state
   */
  open: PropTypes.bool,
  /**
   * Define the action to by applied
   */
  mode: PropTypes.oneOf(['create', 'update']),
  /**
   * Close click handler
   */
  handleClose: PropTypes.func,
  /**
   * Create click handler
   */
  handleCreate: PropTypes.func,
  /**
   * Document type id
   */
  document_type_id: PropTypes.number,
  /**
   * Documents's description
   */
  description: PropTypes.string,
};

DocumentModal.defaultProps = {
  open: true,
  handleClose: undefined,
  handleCreate: undefined,
  name: undefined,
  mode: 'create',
  asyncErrors: {},
};

export default withFormik({
  mapPropsToValues: (props) => ({
    document_type_id: props.document_type_id,
    description: props.description,
  }),
  displayName: 'DocumentModalForm',
  validationSchema: () => {
    const DocumentSchema = Yup.object().shape({
      document_type_id: Yup.string().required(i18n.t('common.required_field')).nullable(),
      description: Yup.string().nullable(),
    });
    return DocumentSchema;
  },
  handleSubmit: (values, { props: { handleCreate } }) =>
    handleCreate(values.document_type_id, values?.description),
})(DocumentModal);
